"use client";

import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart } from "./CartContext";
import { useAuth } from "@/contexts/AuthContext";
import CartIcon from "../icons/CartIcon";
import TrashIcon from "../icons/TrashIcon";
import InfoIcon from "../icons/InfoIcon";
import ModalCloseButton from "../ui/ModalCloseButton";
import ModalPortal from "../ui/ModalPortal";
import SearchIcon from "../icons/SearchIcon";
import ScrollFade from "../ui/ScrollFade";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import { extractVariants } from "@/lib/variant-extractor";
import { computeOrderTotals, ORDER_TAX_LABEL } from "@/lib/pricing/orderTotals";
import {
  DANK_OPEN_DELIVERY_POSTAL_MODAL,
  DANK_OPEN_PICKUP_STORE_MODAL,
} from "@/lib/header-events";
import {
  PRE_CHECKOUT_DELIVERY_VARIABLE_NOTE,
  // PRE_CHECKOUT_ESTIMATE_FOOTNOTE,
} from "@/lib/pricing/pre-checkout-estimate";
import {
  getCartValueBucket,
  trackFunnelEvent,
  trackFunnelEventOnce,
} from "@/lib/analytics/funnel";
import { tagCartConfidenceUx } from "@/lib/analytics/ux-initiatives";
import { rankStoreRecommendations } from "@/lib/recommendations";
import { ProductCard } from "@/components";
import { CheckoutIdempotencySession, checkoutJsonHeaders } from "@/lib/idempotency";
import { logUxApiRequestId } from "@/lib/api/log-api-request-correlation";
import { newClientReferenceId, readUxRequestId } from "@/lib/ux-reference-id";
import { CheckoutRecoveryError } from "@/components/checkout/CheckoutRecoveryError";
import { PromoSavingsBanner } from "@/components/checkout/PromoSavingsBanner";
import { usePromoCartOptimization } from "@/hooks/usePromoCartOptimization";
import { Button } from "@/components";
import { useScrollLock } from "@/lib/use-scroll-lock";
import {
  CTA_OPEN_FULL_CHECKOUT,
  CTA_PROCEED_CHECKOUT,
  CTA_SWITCH_MAIL_ORDER,
  CTA_TRY_AGAIN,
  ERROR_TITLE_CART_CHECKOUT_FAILED,
} from "@/lib/ux-copy";

// Helper functions for merchant cards
function ensureAbsoluteImageUrl(uri: string | null | undefined) {
  const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
  if (!uri) return null;
  if (uri.startsWith("http://") || uri.startsWith("https://")) return uri;
  const clean = uri.startsWith("/") ? uri : `/${uri}`;
  return `${API_BASE}${clean}`;
}

function extractColorsFromProduct(product: any): string[] {
  const colors = new Set<string>();
  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];
  
  groupProducts.forEach((gp: any) => {
    const gpName = gp?.name || gp?.product?.name || "";
    const gpBrand = gp?.brandName || gp?.product?.brandName || product?.brandName || null;
    const gpClassification = gp?.classificationName || gp?.product?.classificationName || product?.classificationName;
    const sizeOrWeight = gp?.sizeOrWeight ? String(gp.sizeOrWeight) : null;
    
    const variants = extractVariants(gpName, gpClassification, gpBrand, sizeOrWeight);
    if (variants.color) {
      colors.add(variants.color);
    }
  });
  
  return Array.from(colors).sort();
}

function extractSizesFromProduct(product: any): string[] {
  const sizes = new Set<string>();
  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];
  
  groupProducts.forEach((gp: any) => {
    const gpName = gp?.name || gp?.product?.name || "";
    const gpBrand = gp?.brandName || gp?.product?.brandName || product?.brandName || null;
    const gpClassification = gp?.classificationName || gp?.product?.classificationName || product?.classificationName;
    const sizeOrWeight = gp?.sizeOrWeight ? String(gp.sizeOrWeight) : null;
    
    const variants = extractVariants(gpName, gpClassification, gpBrand, sizeOrWeight);
    if (variants.size) {
      sizes.add(variants.size);
    }
  });
  
  return Array.from(sizes).sort((a, b) => {
    const aNum = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
    const bNum = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
    if (aNum !== 0 || bNum !== 0) return aNum - bNum;
    const sizeOrder = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl'];
    const aIndex = sizeOrder.findIndex(size => a.toLowerCase().includes(size));
    const bIndex = sizeOrder.findIndex(size => b.toLowerCase().includes(size));
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });
}

function getSelectedVariantOption(product: any, selectedColor: string | null, selectedSize: string | null) {
  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];
  
  for (const gp of groupProducts) {
    const gpAvail = Array.isArray(gp?.availabilities) && gp.availabilities.length > 0 ? gp.availabilities[0] : null;
    if (!gpAvail || Number(gpAvail.inStock || 0) <= 0) continue;
    
    const gpName = gp?.name || gp?.product?.name || "";
    const gpBrand = gp?.brandName || gp?.product?.brandName || product?.brandName || null;
    const gpClassification = gp?.classificationName || gp?.product?.classificationName || product?.classificationName;
    const sizeOrWeight = gp?.sizeOrWeight ? String(gp.sizeOrWeight) : null;
    
    const variants = extractVariants(gpName, gpClassification, gpBrand, sizeOrWeight);
    
    const colorMatch = !selectedColor || variants.color === selectedColor;
    const sizeMatch = !selectedSize || variants.size === selectedSize;
    
    if (colorMatch && sizeMatch) {
      const price = gpAvail.price !== null && gpAvail.price !== undefined
        ? Number(gpAvail.price)
        : Number(gpAvail.unitCost || 0);
      
      return {
        productId: String(gp?.id || gp?.productId || ""),
        packageId: gp?.slug || null,
        actualPackageId: gpAvail?.packageId || null,
        price: Number.isFinite(price) ? price : 0,
        inStock: Number(gpAvail.inStock || 0),
        heroShotUri: gp?.heroShotUri || null,
        color: variants.color,
        size: variants.size,
      };
    }
  }
  
  // Fallback to first available option
  const firstGp = groupProducts.find((gp: any) => {
    const gpAvail = Array.isArray(gp?.availabilities) && gp.availabilities.length > 0 ? gp.availabilities[0] : null;
    return gpAvail && Number(gpAvail.inStock || 0) > 0;
  });
  
  if (firstGp) {
    const gpAvail = firstGp.availabilities[0];
    const price = gpAvail.price !== null && gpAvail.price !== undefined
      ? Number(gpAvail.price)
      : Number(gpAvail.unitCost || 0);
    
    return {
      productId: String(firstGp?.id || firstGp?.productId || ""),
      packageId: firstGp?.slug || null,
      actualPackageId: gpAvail?.packageId || null,
      price: Number.isFinite(price) ? price : 0,
      inStock: Number(gpAvail.inStock || 0),
      heroShotUri: firstGp?.heroShotUri || null,
      color: null,
      size: null,
    };
  }
  
  return null;
}

export default function CartDrawer() {
  const router = useRouter();
  const {
    state,
    addItem,
    removeItem,
    updateQty,
    total,
    setStore,
    isCartOpen,
    openCart,
    closeCart,
    setPromoCode,
    clear,
  } = useCart();
  useScrollLock(isCartOpen);
  const { user, isAuthenticated } = useAuth();
  const API_BASE =
    process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
  const [companyId, setCompanyId] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  // The quick cart drawer never collects card data. Card payments go through the
  // full /checkout page (Moneris Hosted Tokenization), so no card state here.
  const lastPickupTaskIdRef = useRef<string | undefined>(undefined);
  const checkoutIdemRef = useRef(new CheckoutIdempotencySession());
  const cartCheckoutErrorResponseRef = useRef<Response | null>(null);
  const cartIdemFingerprint = useMemo(
    () =>
      JSON.stringify(
        state.items.map((it) => ({
          id: it.catalogItemId,
          q: it.quantity,
          pkg: (it as { actualPackageId?: string }).actualPackageId ?? it.packageId ?? null,
        }))
      ),
    [state.items]
  );
  const cartIdemFpPrev = useRef(cartIdemFingerprint);
  useEffect(() => {
    if (cartIdemFpPrev.current !== cartIdemFingerprint) {
      checkoutIdemRef.current.reset();
      cartIdemFpPrev.current = cartIdemFingerprint;
    }
  }, [cartIdemFingerprint]);
  /** Use cart context only, local mode was never updated and broke delivery pricing vs checkout. */
  const mode = state.fulfillmentMode;
  const [deliveryType, setDeliveryType] = useState<"same-day" | "mail-order">(
    "same-day"
  );
  const [deliveryName, setDeliveryName] = useState("");
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryUnit, setDeliveryUnit] = useState("");
  const [deliveryPostalCode, setDeliveryPostalCode] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasCheckoutRecovery, setHasCheckoutRecovery] = useState(false);
  const [banner, setBanner] = useState<
    | { type: "success"; text: string }
    | { type: "error"; text: string; referenceId: string }
    | null
  >(null);
  const [payInStore, setPayInStore] = useState(false);
  const [thcLimit, setThcLimit] = useState<{
    current: number;
    max: number;
  } | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeLoading, setDeliveryFeeLoading] = useState(false);
  // Removed canChooseDeliveryType - delivery type is now automatically determined
  const [sameDayAvailable, setSameDayAvailable] = useState(true);
  const [sameDayAvailableReason, setSameDayAvailableReason] = useState<
    string | undefined
  >();

  // Promo code
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);

  // Product details for cart items
  type CartItemDetails = {
    productId: string;
    packageId?: string | null;
    brandName?: string | null;
    productName: string;
    imageUrl?: string | null;
    thcPercent?: number | null;
    thcMg?: number | null;
    thcMin?: number | null;
    thcMax?: number | null;
    cbdPercent?: number | null;
    cbdMg?: number | null;
    cbdMin?: number | null;
    cbdMax?: number | null;
    compareAtPrice?: number | null;
    unitCost?: number | null;
    classificationName?: string | null;
  };
  const [cartItemDetails, setCartItemDetails] = useState<
    Record<string, CartItemDetails>
  >({});
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);

  // Store details
  const [storeDetails, setStoreDetails] = useState<{
    name: string;
    city?: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/config/public`);
        const j = await res.json();
        if (j?.companyId) setCompanyId(String(j.companyId));
      } catch (error) {
        // Silently fail - config is optional
        if (process.env.NODE_ENV === "development") {
          console.warn("[CartDrawer] Failed to fetch config:", error);
        }
      }
    })();
  }, [API_BASE]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const localHas = (() => {
      try {
        return localStorage.getItem("dank:checkout-recovery:v1") != null;
      } catch {
        return false;
      }
    })();
    setHasCheckoutRecovery(localHas);

    if (!user) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/checkout-recovery`, {
          credentials: "include",
        });
        const j = await res.json();
        if (res.ok && j?.success) {
          setHasCheckoutRecovery(!!j.recovery);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [user, API_BASE]);

  // Fallback: derive Location ID from URL when not yet set by store page
  useEffect(() => {
    if (!state.storeCovaLocationId && typeof window !== "undefined") {
      const match = window.location.pathname.match(/\/store\/(\d+)/);
      if (match) {
        const id = Number(match[1]);
        if (Number.isFinite(id) && id > 0) setStore(id);
      }
    }
  }, [state.storeCovaLocationId, setStore]);

  // Auto-fill fields from user account when user is logged in
  useEffect(() => {
    if (user) {
      // Set customer ID from COVA customer ID
      if (user.covaCustomerId && !customerId) {
        setCustomerId(user.covaCustomerId);
      }

      // Auto-fill delivery fields for delivery mode
      if (mode === "delivery") {
        if (user.firstName && user.lastName && !deliveryName) {
          setDeliveryName(`${user.firstName} ${user.lastName}`.trim());
        }
        if (user.phone && !deliveryPhone) {
          setDeliveryPhone(user.phone);
        }
        if (user.streetAddress && !deliveryAddress) {
          setDeliveryAddress(user.streetAddress);
        }
        if (user.postalCode && !deliveryPostalCode) {
          setDeliveryPostalCode(user.postalCode);
        }
      }

      // Auto-fill email for both pickup and delivery
      if (user.email && !customerEmail) {
        setCustomerEmail(user.email);
      }

      // Auto-fill name and phone for pickup notifications
      if (mode === "pickup") {
        if (user.firstName && user.lastName && !deliveryName) {
          setDeliveryName(`${user.firstName} ${user.lastName}`.trim());
        }
        if (user.phone && !deliveryPhone) {
          setDeliveryPhone(user.phone);
        }
      }
    }
  }, [
    user,
    mode,
    customerId,
    deliveryName,
    deliveryPhone,
    deliveryAddress,
    deliveryPostalCode,
    customerEmail,
  ]);

  const confirmCheckout = async () => {
    try {
      cartCheckoutErrorResponseRef.current = null;
      setBanner(null);
      setSubmitting(true);
      setStatus("Processing…");
      const amount = total;

      // Minimum order enforcement: $50 for both pickup and delivery
      const MIN_ORDER_AMOUNT = 50;
      if (amount < MIN_ORDER_AMOUNT) {
        throw new Error(
          `Minimum order amount is $${MIN_ORDER_AMOUNT}. Current total: $${amount.toFixed(
            2
          )}`
        );
      }

      // Time window enforcement for same-day delivery
      if (
        mode === "delivery" &&
        deliveryType === "same-day" &&
        !sameDayAvailable
      ) {
        throw new Error(
          sameDayAvailableReason ||
            "Same-day delivery is not available at this time. Please choose mail order or try again during delivery hours (9 AM - 11 PM)."
        );
      }

      if (!companyId) throw new Error("Company ID missing.");
      if (!state.storeCovaLocationId)
        throw new Error("Location ID missing. Select a store.");

      // Use authenticated user's COVA customer ID if available, otherwise use form value
      const finalCustomerId = user?.covaCustomerId || customerId.trim();
      if (!finalCustomerId) {
        throw new Error(
          "Customer ID is required. Please sign in or enter a customer ID."
        );
      }

      // 1) Payment processing. The drawer only handles pay-in-store here; it
      // never takes card details (no raw card ever leaves the browser). Card
      // orders are completed on the full /checkout page via Moneris Hosted
      // Tokenization, so a non-pay-in-store order here just gets a placeholder.
      let txnNumber: string;
      let last4: string | undefined = undefined;

      if (payInStore) {
        txnNumber = `PAY-IN-STORE-${Date.now()}`;
      } else {
        txnNumber = `TEST-${Date.now()}`;
      }
      // 2) Create order
      // For delivery orders, resolve postal code to get fulfillment location
      // Use authenticated user's postal code if available
      const finalDeliveryPostalCode =
        user?.postalCode || deliveryPostalCode || "";
      let fulfillmentLocationForOrder: string | undefined;
      if (mode === "delivery" && finalDeliveryPostalCode) {
        try {
          // Use new routing API
          const resolveRes = await fetch(`${API_BASE}/api/delivery/routing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              postalCode: finalDeliveryPostalCode,
              cartSubtotal: total,
            }),
          });
          const resolveData = await resolveRes.json();
          if (resolveData?.success && resolveData?.store) {
            fulfillmentLocationForOrder = resolveData.store.name || undefined;
          } else if (resolveData?.success && resolveData?.resolved) {
            // Fallback for legacy API format
            fulfillmentLocationForOrder =
              resolveData.resolved.storeName || undefined;
          }
        } catch (resolveError) {
          // Silently fail - postal code resolution is optional for order notes
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[CartDrawer] Failed to resolve postal code for order notes:",
              resolveError
            );
          }
        }
      }

      const lines = state.items.map((it) => ({
        name: it.name,
        catalogItemId: it.catalogItemId,
        quantity: it.quantity,
        // Use actualPackageId if available (for backend), otherwise fall back to packageId (variant)
        packageId: (it as any).actualPackageId ?? it.packageId ?? undefined,
        lineDollarAmount: it.price * it.quantity,
      }));

      // Use authenticated user data if available, otherwise use form values
      const finalCustomerEmail = user?.email || customerEmail || undefined;
      const finalCustomerPhone = user?.phone || deliveryPhone || undefined;
      const finalCustomerName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : deliveryName || undefined;
      const deliveryStreetLine = (
        user?.streetAddress ||
        deliveryAddress ||
        ""
      ).trim();
      const deliveryUnitTrimmed = deliveryUnit.trim();
      const finalDeliveryAddress =
        mode === "delivery"
          ? [deliveryStreetLine, deliveryUnitTrimmed ? `Unit ${deliveryUnitTrimmed}` : ""]
              .filter(Boolean)
              .join(", ") || undefined
          : undefined;

      const orderPayload: any = {
        pricingMode: "TIP",
        companyId,
        locationId: state.storeCovaLocationId,
        customerId: finalCustomerId,
        lines,
        orderReference: `Web Order ${Date.now()}`,
        isDelivery: mode === "delivery",
        deliveryType: mode === "delivery" ? deliveryType : undefined, // Add delivery type for mail orders
        customerEmail: finalCustomerEmail, // Use authenticated user email or form value
        customerPhone: finalCustomerPhone, // Use authenticated user phone or form value
        customerName: finalCustomerName, // Use authenticated user name or form value
        deliveryAddress: finalDeliveryAddress, // Use authenticated user address or form value
        fulfillmentLocation: fulfillmentLocationForOrder, // Add fulfillment location for order notes
      };
      const orderRes = await fetch(`${API_BASE}/api/orders/create`, {
        method: "POST",
        headers: checkoutJsonHeaders(checkoutIdemRef.current.key("create")),
        body: JSON.stringify(orderPayload),
      });
      const order = await orderRes.json();
      if (!orderRes.ok) logUxApiRequestId("orders_create", orderRes, "http_error");
      else if (!order?.success) logUxApiRequestId("orders_create", orderRes, "business_error");
      if (!order?.success) {
        cartCheckoutErrorResponseRef.current = orderRes;
        throw new Error(order?.error || "Order create failed");
      }
      const salesOrderId = order?.salesOrderId;
      // 3) Payment intake
      const intakeRes = await fetch(`${API_BASE}/api/orders/payment`, {
        method: "POST",
        headers: checkoutJsonHeaders(checkoutIdemRef.current.key("payment")),
        body: JSON.stringify({
          companyId,
          salesOrderId,
          amount,
          preAuthorizationToken: txnNumber,
          last4Digits: last4,
        }),
      });
      const intake = await intakeRes.json();
      if (!intakeRes.ok) logUxApiRequestId("orders_payment", intakeRes, "http_error");
      else if (!intake?.success) logUxApiRequestId("orders_payment", intakeRes, "business_error");
      if (!intake?.success) {
        cartCheckoutErrorResponseRef.current = intakeRes;
        throw new Error(intake?.error || "Payment intake failed");
      }
      // No Moneris capture here: the drawer never runs a card pre-auth, so
      // there is nothing to capture. Card capture happens on /checkout.
      // 5) Delivery via Onfleet (if same-day delivery) or Mail Order (if mail order)
      let onfleetTaskId: string | undefined;
      let onfleetTrackingUrl: string | undefined;
      let mailOrderTrackingNumber: string | undefined;
      let fulfillmentLocation: string | undefined;
      if (mode === "delivery") {
        if (deliveryType === "same-day") {
          try {
            // Resolve postal code to store for fulfillment location
            let fulfillmentStoreId = state.storeCovaLocationId;
            let fulfillmentStoreName = "Store";
            let fulfillmentStoreAddress = "";

            if (finalDeliveryPostalCode) {
              try {
                const resolveRes = await fetch(
                  `${API_BASE}/api/locations/resolve?postal=${encodeURIComponent(
                    finalDeliveryPostalCode
                  )}`
                );
                const resolveData = await resolveRes.json();
                if (resolveData?.success && resolveData?.store) {
                  fulfillmentStoreId =
                    resolveData.store.covaLocationId ||
                    state.storeCovaLocationId;
                  fulfillmentStoreName = resolveData.store.name || "Store";
                  fulfillmentLocation = fulfillmentStoreName;
                } else if (resolveData?.success && resolveData?.resolved) {
                  // Fallback for legacy API format
                  fulfillmentStoreId =
                    resolveData.resolved.covaLocationId ||
                    state.storeCovaLocationId;
                  fulfillmentStoreName =
                    resolveData.resolved.storeName || "Store";
                  fulfillmentLocation = fulfillmentStoreName;
                }
              } catch (resolveError) {
                // Silently fail - will use default store
                if (process.env.NODE_ENV === "development") {
                  console.warn(
                    "[CartDrawer] Failed to resolve postal code to store:",
                    resolveError
                  );
                }
              }
            }

            // Get store details for unified delivery
            let storeDetails: any = null;
            try {
              const storeRes = await fetch(
                `${API_BASE}/api/stores/${fulfillmentStoreId}`
              );
              const storeData = await storeRes.json();
              if (storeData?.success && storeData?.store) {
                storeDetails = storeData.store;
                fulfillmentStoreName =
                  storeDetails.name || fulfillmentStoreName;
                // Build store address
                const addressParts = [
                  storeDetails.address,
                  storeDetails.city,
                  storeDetails.province,
                  storeDetails.postalCode,
                ].filter(Boolean);
                fulfillmentStoreAddress = addressParts.join(", ");
              }
            } catch (storeError) {
              // Silently fail - will use fallback address
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "[CartDrawer] Failed to get store details:",
                  storeError
                );
              }
              // Fallback: use basic address
              fulfillmentStoreAddress = deliveryAddress; // Will be updated if store details are available
            }

            // Format order lines for unified delivery
            const orderLines = state.items.map((item) => ({
              name: item.name,
              quantity: item.quantity,
              price: item.price,
            }));

            // Build store address for Onfleet (use unparsed format)
            const storeAddress =
              fulfillmentStoreAddress ||
              `${storeDetails?.address || ""}, ${storeDetails?.city || ""}, ${
                storeDetails?.province || ""
              } ${storeDetails?.postalCode || ""}`.trim();

            // Create unified delivery (pickup + delivery tasks, linked)
            // Use authenticated user data if available
            const onfleetCustomerName =
              finalCustomerName || deliveryName || "Customer";
            const onfleetCustomerPhone =
              finalCustomerPhone || deliveryPhone || "";
            const onfleetCustomerAddress =
              finalDeliveryAddress || deliveryAddress || "";
            const onfleetCustomerEmail =
              finalCustomerEmail || customerEmail || undefined;

            const unifiedPayload = {
              store: {
                name: fulfillmentStoreName,
                address: { unparsed: storeAddress },
                phone: storeDetails?.phone || "",
                notes: `Pickup order ${salesOrderId} from ${fulfillmentStoreName}`,
              },
              customer: {
                name: onfleetCustomerName,
                phone: onfleetCustomerPhone,
                address: { unparsed: onfleetCustomerAddress },
                email: onfleetCustomerEmail,
                notes: `Delivery for order ${salesOrderId}`,
              },
              salesOrderId,
              orderNotes: `Order ${salesOrderId}`,
              orderLines,
              orderTotal: total,
              // On-demand delivery: no scheduling fields
              // For scheduled delivery, dispatcher can manually set completeAfter/completeBefore via API
              autoAssign: true,
              metadata: [
                {
                  name: "fulfillmentLocation",
                  type: "string",
                  value: fulfillmentLocation || fulfillmentStoreName,
                },
                ...(deliveryUnitTrimmed
                  ? [
                      {
                        name: "deliveryUnitLine",
                        type: "string" as const,
                        value: deliveryUnitTrimmed,
                      },
                    ]
                  : []),
              ],
            };

            const unifiedRes = await fetch(
              `${API_BASE}/api/onfleet/deliveries/unified`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(unifiedPayload),
              }
            );
            const unifiedData = await unifiedRes.json();
            if (!unifiedData?.success)
              throw new Error(
                unifiedData?.error || "Unified delivery creation failed"
              );

            // Extract pickup and delivery task IDs and tracking URL
            const pickupTaskId =
              unifiedData?.pickupTask?.id || unifiedData?.pickupTask?.taskId;
            onfleetTaskId =
              unifiedData?.deliveryTask?.id ||
              unifiedData?.deliveryTask?.taskId;
            onfleetTrackingUrl =
              unifiedData?.trackingUrl ||
              unifiedData?.deliveryTask?.trackingURL ||
              unifiedData?.deliveryTask?.trackingUrl;

            // Store pickup task ID for order update
            lastPickupTaskIdRef.current = pickupTaskId;

            if (!onfleetTrackingUrl && process.env.NODE_ENV === "development") {
              console.warn(
                "[CartDrawer] Unified delivery created but trackingURL is missing"
              );
            }
          } catch (onfleetError: unknown) {
            // Log error but don't fail the order - order should still be created
            if (process.env.NODE_ENV === "development") {
              console.error(
                "[CartDrawer] Unified delivery creation failed:",
                onfleetError
              );
            }
            // Continue without Onfleet task - order will still be created
          }
        } else if (deliveryType === "mail-order") {
          // Mail order delivery - create shipping label via Canada Post
          try {
            // Use authenticated user data for mail order
            const mailOrderName =
              finalCustomerName || deliveryName || "Customer";
            const mailOrderPhone = finalCustomerPhone || deliveryPhone || "";
            const mailOrderAddress =
              finalDeliveryAddress || deliveryAddress || "";
            const mailOrderEmail =
              finalCustomerEmail || customerEmail || undefined;
            const mailOrderPostalCode =
              user?.postalCode || deliveryPostalCode || "";

            const shipmentRes = await fetch(
              `${API_BASE}/api/canada-post/shipment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  destinationPostalCode: mailOrderPostalCode,
                  recipientName: mailOrderName,
                  recipientPhone: mailOrderPhone,
                  recipientAddress: mailOrderAddress,
                  recipientEmail: mailOrderEmail,
                  weight: 1, // Default weight, can be calculated from cart items
                  salesOrderId: salesOrderId, // Pass salesOrderId for SMS notifications
                }),
              }
            );
            const shipment = await shipmentRes.json();
            if (shipment?.success && shipment?.shipment?.trackingNumber) {
              mailOrderTrackingNumber = shipment.shipment.trackingNumber;
            } else {
              // Fallback: generate placeholder tracking number
              mailOrderTrackingNumber = `CP${Date.now().toString().slice(-10)}`;
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "[CartDrawer] Canada Post API failed, using placeholder tracking:",
                  mailOrderTrackingNumber
                );
              }
            }
          } catch (mailOrderError: unknown) {
            // Log error but don't fail the order
            if (process.env.NODE_ENV === "development") {
              console.error(
                "[CartDrawer] Mail order creation failed:",
                mailOrderError
              );
            }
            // Fallback: generate placeholder tracking number
            mailOrderTrackingNumber = `CP${Date.now().toString().slice(-10)}`;
            // Continue without mail order tracking - order will still be created
          }
        }
      }
      // 6) Update order with Onfleet task ID (if same-day delivery and task was created)
      if (mode === "delivery" && deliveryType === "same-day" && onfleetTaskId) {
        try {
          // For unified delivery, include both pickup and delivery task IDs
          const pickupTaskId = lastPickupTaskIdRef.current;
          const updatePayload: any = {
            salesOrderId,
            deliveryTaskId: onfleetTaskId, // Use delivery task ID as primary
            onfleetTrackingUrl,
          };

          // Include pickup task ID if available (from unified delivery)
          if (pickupTaskId) {
            updatePayload.pickupTaskId = pickupTaskId;
          }

          await fetch(`${API_BASE}/api/orders/update-onfleet`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatePayload),
          });

          // Clean up
          lastPickupTaskIdRef.current = undefined;
        } catch (updateError: unknown) {
          // Log error but don't fail the order
          if (process.env.NODE_ENV === "development") {
            console.error(
              "[CartDrawer] Failed to update order with Onfleet info:",
              updateError
            );
          }
        }
      }
      // 6b) Update order with mail order tracking (if mail order and tracking was created)
      if (
        mode === "delivery" &&
        deliveryType === "mail-order" &&
        mailOrderTrackingNumber
      ) {
        try {
          await fetch(`${API_BASE}/api/orders/update-mail-order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              salesOrderId,
              mailOrderTrackingNumber,
              deliveryPostalCode: finalDeliveryPostalCode,
              customerEmail: finalCustomerEmail || customerEmail || undefined, // Use authenticated user email or form value
              customerPhone: finalCustomerPhone || deliveryPhone || undefined, // Use authenticated user phone or form value
            }),
          });
        } catch (updateError: unknown) {
          // Log error but don't fail the order
          if (process.env.NODE_ENV === "development") {
            console.error(
              "[CartDrawer] Failed to update order with mail order info:",
              updateError
            );
          }
        }
      }
      // 7) Log order for admin view
      await fetch(`${API_BASE}/api/orders/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          salesOrderId,
          mode,
          locationId: state.storeCovaLocationId,
          amount,
          payInStore,
        }),
      });
      setStatus(
        payInStore
          ? "Order requested, pay in-store when you arrive"
          : "Order requested, awaiting store confirmation"
      );
      setBanner({
        type: "success",
        text: payInStore
          ? `Order requested (#${salesOrderId}). Please pay in-store when you arrive.`
          : `Order requested (#${salesOrderId}). We will confirm when the store approves.`,
      });

      // Clear cart after successful order
      clear();
      checkoutIdemRef.current.reset();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Checkout failed";
      setStatus("");
      const res = cartCheckoutErrorResponseRef.current;
      const referenceId =
        readUxRequestId(res ?? undefined) ?? newClientReferenceId("cart");
      setBanner({ type: "error", text: msg, referenceId });
    } finally {
      setSubmitting(false);
    }
  };
  // Calculate THC limit (Alberta: 30g dried cannabis equivalent per transaction)
  useEffect(() => {
    const calculateTHCLimit = async () => {
      if (state.items.length === 0) {
        setThcLimit(null);
        return;
      }

      try {
        let totalTHCGrams = 0;
        const MAX_THC_LIMIT = 30; // Alberta limit: 30g dried cannabis equivalent

        // Fetch product details for each cart item to calculate THC
        for (const item of state.items) {
          try {
            // Get store location ID (use current store or try to get from URL)
            let locationId = state.storeCovaLocationId;
            if (!locationId && typeof window !== "undefined") {
              const match = window.location.pathname.match(/\/store\/(\d+)/);
              if (match) locationId = Number(match[1]);
            }
            if (!locationId) continue;

            // Fetch product details
            const productRes = await fetch(
              `${API_BASE}/api/store/${locationId}/products/${item.productId}`
            );
            const productData = await productRes.json();

            if (productData?.success && productData?.product) {
              const product = productData.product;

              // Method 1: Use equivalenceDriedFlowerG if available (most accurate)
              if (
                product.equivalenceDriedFlowerG !== undefined &&
                product.equivalenceDriedFlowerG !== null
              ) {
                const equivGrams = Number(product.equivalenceDriedFlowerG);
                if (!isNaN(equivGrams) && equivGrams > 0) {
                  totalTHCGrams += equivGrams * item.quantity;
                  continue;
                }
              }

              // Method 2: Calculate from THC% and netContent
              // Get netContent in grams
              let netContentGrams: number | null = null;
              if (product.netContent) {
                const netContentLower = String(
                  product.netContent
                ).toLowerCase();
                if (netContentLower.includes("g")) {
                  const grams = parseFloat(
                    netContentLower.replace(/[^0-9.]/g, "")
                  );
                  if (!isNaN(grams)) netContentGrams = grams;
                } else if (netContentLower.includes("mg")) {
                  const mg = parseFloat(
                    netContentLower.replace(/[^0-9.]/g, "")
                  );
                  if (!isNaN(mg)) netContentGrams = mg / 1000;
                }
              }

              // Get THC percentage
              let thcPercent: number | null = null;
              if (
                product.thcPercent !== undefined &&
                product.thcPercent !== null
              ) {
                thcPercent = Number(product.thcPercent);
              } else if (
                product.thcMin !== undefined &&
                product.thcMin !== null
              ) {
                // Use average of min/max if available
                const thcMin = Number(product.thcMin);
                const thcMax = product.thcMax ? Number(product.thcMax) : thcMin;
                thcPercent = (thcMin + thcMax) / 2;

                // Convert from UOM if needed
                if (product.thcUom) {
                  const uom = String(product.thcUom).toLowerCase().trim();
                  if (uom === "mg/g" || uom === "mg per g") {
                    thcPercent = thcPercent / 10; // mg/g to %
                  } else if (uom === "mg" && netContentGrams) {
                    // THC mg / (netContent in mg) * 100
                    thcPercent = (thcPercent / (netContentGrams * 1000)) * 100;
                  }
                }
              }

              // Calculate THC grams: (THC% / 100) * netContent in grams * quantity
              if (
                thcPercent !== null &&
                netContentGrams !== null &&
                !isNaN(thcPercent) &&
                !isNaN(netContentGrams)
              ) {
                const thcGramsPerUnit = (thcPercent / 100) * netContentGrams;
                totalTHCGrams += thcGramsPerUnit * item.quantity;
              }
            }
          } catch (err) {
            // Silently fail for individual products - continue with other items
            if (process.env.NODE_ENV === "development") {
              console.warn(
                `[CartDrawer] Failed to fetch product ${item.productId} for THC calculation:`,
                err
              );
            }
            // Continue with other items
          }
        }

        setThcLimit({
          current: Math.round(totalTHCGrams * 10) / 10, // Round to 1 decimal
          max: MAX_THC_LIMIT,
        });
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("[CartDrawer] Failed to calculate THC limit:", err);
        }
        setThcLimit(null);
      }
    };

    calculateTHCLimit();
  }, [state.items, state.storeCovaLocationId, API_BASE]);

  // Automatically determine delivery type based on postal code
  useEffect(() => {
    if (mode === "delivery" && state.storeCovaLocationId) {
      const postalForCheck = user?.postalCode || deliveryPostalCode || "";

      if (postalForCheck) {
        // Resolve postal code to determine delivery type eligibility using new routing API
        const determineDeliveryType = async () => {
          try {
            const res = await fetch(`${API_BASE}/api/delivery/routing`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                postalCode: postalForCheck,
                cartSubtotal: total,
              }),
            });
            const data = await res.json();

            if (data?.success) {
              // Use the delivery type from routing result
              if (data.deliveryType === "same-day" && data.deliveryAvailable) {
                setDeliveryType("same-day");
              } else if (data.deliveryType === "mail-order") {
                setDeliveryType("mail-order");
              } else {
                setDeliveryType("mail-order");
              }
            } else {
              // If resolution fails, default to mail-order
              setDeliveryType("mail-order");
            }
          } catch (error) {
            // Default to mail-order if resolution fails
            if (process.env.NODE_ENV === "development") {
              console.error(
                "[CartDrawer] Failed to resolve postal code for delivery type:",
                error
              );
            }
            setDeliveryType("mail-order");
          }
        };

        determineDeliveryType();
      } else {
        // No postal code yet, default to same-day (will be updated when postal code is entered)
        setDeliveryType("same-day");
      }
    }
  }, [
    mode,
    deliveryPostalCode,
    user?.postalCode,
    state.storeCovaLocationId,
    API_BASE,
    total,
  ]);

  // Fetch delivery fee when delivery mode/type/postal code changes using new routing API
  useEffect(() => {
    if (mode === "delivery" && state.storeCovaLocationId) {
      const fetchDeliveryFee = async () => {
        try {
          // Use authenticated user's postal code if available for delivery fee calculation
          const postalForFee = user?.postalCode || deliveryPostalCode || "";
          if (!postalForFee) {
            setDeliveryFee(0);
            setDeliveryFeeLoading(false);
            return;
          }

          setDeliveryFeeLoading(true);

          // Use new routing API
          const res = await fetch(`${API_BASE}/api/delivery/routing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              postalCode: postalForFee,
              cartSubtotal: total,
            }),
          });
          const data = await res.json();
          if (data?.success) {
            setDeliveryFee(data.fee || 0);
            setSameDayAvailable(data.deliveryAvailable !== false);
            setSameDayAvailableReason(
              data.deliveryAvailable
                ? undefined
                : data.nextAvailableTime
                ? `Delivery available at ${new Date(
                    data.nextAvailableTime
                  ).toLocaleString()}`
                : "Delivery not available at this time"
            );
          } else {
            // Fallback to legacy API
            const params = new URLSearchParams({
              orderTotal: total.toFixed(2),
              deliveryType: deliveryType,
              locationId: String(state.storeCovaLocationId),
              postalCode: postalForFee,
            });
            const legacyRes = await fetch(
              `${API_BASE}/api/delivery/fee?${params}`
            );
            const legacyData = await legacyRes.json();
            if (legacyData?.success) {
              setDeliveryFee(legacyData.fee || 0);
              setSameDayAvailable(legacyData.sameDayAvailable !== false);
              setSameDayAvailableReason(legacyData.sameDayAvailableReason);
            }
          }
        } catch (error) {
          // Silently fail - delivery fee will default to 0
          if (process.env.NODE_ENV === "development") {
            console.error("[CartDrawer] Failed to fetch delivery fee:", error);
          }
        } finally {
          setDeliveryFeeLoading(false);
        }
      };
      fetchDeliveryFee();
    } else {
      setDeliveryFee(0);
      setDeliveryFeeLoading(false);
      setSameDayAvailable(true);
    }
  }, [
    mode,
    deliveryType,
    total,
    deliveryPostalCode,
    user?.postalCode,
    state.storeCovaLocationId,
    API_BASE,
  ]);

  // Get promo discount from context
  const promoDiscount = state.promoCode?.discountAmount || 0;

  // Calculate product-level discounts (price - unitCost) * quantity for all items
  // Use useMemo to recalculate when cartItemDetails or items change
  const productDiscount = useMemo(() => {
    return state.items.reduce((sum, item) => {
      const key = `${item.productId}:${item.packageId ?? ""}`;
      const details = cartItemDetails[key];
      if (details?.unitCost && item.price > details.unitCost) {
        const itemDiscount = (item.price - details.unitCost) * item.quantity;
        return sum + itemDiscount;
      }
      return sum;
    }, 0);
  }, [state.items, cartItemDetails]);

  const { tax, finalTotal } = computeOrderTotals({
    lineSubtotal: total,
    promoDiscount,
    deliveryFee: mode === "delivery" ? deliveryFee : 0,
    tipAmount: 0,
  });

  const postalForEstimate =
    user?.postalCode || deliveryPostalCode || "";

  usePromoCartOptimization({
    apiBase: API_BASE.replace(/\/$/, ""),
    orderAmount: total,
    fulfillmentMode: mode,
    userId: user?.id,
    cartHasItems: state.items.length > 0,
    promoCode: state.promoCode,
    setPromoCode,
    onPromoInvalidated: (msg) => {
      setPromoCodeError(msg);
    },
    onPromoResolved: () => {
      setPromoCodeError(null);
    },
  });

  const thcPickupBlocked =
    mode === "pickup" && !!thcLimit && thcLimit.current > thcLimit.max;

  // Handle promo code validation
  const handleApplyPromoCode = async () => {
    if (!promoCodeInput.trim()) return;

    setPromoCodeError(null);
    try {
      const res = await fetch(`${API_BASE}/api/promo/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: promoCodeInput.trim(),
          orderAmount: total,
          fulfillmentMode: mode,
          userId: user?.id,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setPromoCode({
          code: (data.promoCode.code || promoCodeInput).trim().toUpperCase(),
          promoCodeId: data.promoCode.id,
          discountAmount: data.promoCode.discountAmount || 0,
          source: "manual",
        });
        setPromoCodeError(null);
        setPromoCodeInput("");
      } else {
        setPromoCodeError(data.error || "Invalid promo code");
      }
    } catch (err) {
      setPromoCodeError("Failed to validate promo code");
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode(null);
    setPromoCodeInput("");
    setPromoCodeError(null);
  };

  // Load promo code from context on mount
  useEffect(() => {
    if (state.promoCode) {
      setPromoCodeInput(state.promoCode.code || "");
    }
  }, [state.promoCode]);

  // Fetch store details
  useEffect(() => {
    if (state.storeCovaLocationId) {
      (async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/stores/${state.storeCovaLocationId}`,
            { cache: "no-store" }
          );
          if (res.ok) {
            const data = await res.json();
            if (data?.success && data?.store) {
              setStoreDetails({
                name: data.store.name || "",
                city: data.store.city || undefined,
              });
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[CartDrawer] Failed to fetch store details:", err);
          }
        }
      })();
    } else {
      setStoreDetails(null);
    }
  }, [state.storeCovaLocationId, API_BASE]);

  // Fetch product details for cart items
  useEffect(() => {
    if (state.items.length === 0) {
      setCartItemDetails({});
      return;
    }

    setLoadingProductDetails(true);
    const fetchProductDetails = async () => {
      const details: Record<string, CartItemDetails> = {};

      for (const item of state.items) {
        const key = `${item.productId}:${item.packageId ?? ""}`;

        // Skip if already loaded
        if (details[key]) continue;

        try {
          if (!state.storeCovaLocationId) continue;

          const res = await fetch(
            `${API_BASE}/api/store/${state.storeCovaLocationId}/products/${item.productId}`,
            { cache: "no-store" }
          );
          if (res.ok) {
            const data = await res.json();
            if (data?.success && data?.product) {
              const product = data.product;

              // Find the specific size option if packageId is provided
              let sizeOption = null;
              if (item.packageId && product.sizeOptions) {
                sizeOption = product.sizeOptions.find(
                  (opt: any) => opt.packageId === item.packageId
                );
              }

              // Find unitCost from groupProducts if available (for variant-specific unitCost)
              let unitCost = null;
              if (item.packageId && product.groupProducts) {
                const groupProduct = product.groupProducts.find(
                  (gp: any) => gp.packageId === item.packageId
                );
                if (groupProduct?.unitCost) {
                  unitCost = Number(groupProduct.unitCost);
                }
              }
              // Fallback to sizeOption or product level unitCost
              if (!unitCost) {
                unitCost = sizeOption?.unitCost
                  ? Number(sizeOption.unitCost)
                  : product.unitCost
                  ? Number(product.unitCost)
                  : null;
              }

              details[key] = {
                productId: item.productId,
                packageId: item.packageId ?? null,
                brandName: product.brand?.name || product.brandName || null,
                productName: item.name,
                imageUrl:
                  sizeOption?.heroShotUri ||
                  product.heroShotUri ||
                  product.media?.[0]?.url ||
                  null,
                thcPercent:
                  product.thcPercent !== undefined && product.thcPercent !== null
                    ? Number(product.thcPercent)
                    : null,
                thcMg:
                  product.thcMg !== undefined && product.thcMg !== null
                    ? Number(product.thcMg)
                    : null,
                thcMin: product.thcMin ?? null,
                thcMax: product.thcMax ?? null,
                cbdPercent:
                  product.cbdPercent !== undefined && product.cbdPercent !== null
                    ? Number(product.cbdPercent)
                    : null,
                cbdMg:
                  product.cbdMg !== undefined && product.cbdMg !== null
                    ? Number(product.cbdMg)
                    : null,
                cbdMin: product.cbdMin ?? null,
                cbdMax: product.cbdMax ?? null,
                compareAtPrice:
                  sizeOption?.compareAtPrice || product.compareAtPrice || null,
                unitCost: unitCost,
                classificationName: product.classificationName ?? null,
              };
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `[CartDrawer] Failed to fetch product ${item.productId}:`,
              err
            );
          }
          // Fallback to basic info
          details[key] = {
            productId: item.productId,
            packageId: item.packageId ?? null,
            brandName: null,
            productName: item.name,
            imageUrl: null,
            thcPercent: null,
            thcMg: null,
            thcMin: null,
            thcMax: null,
            cbdPercent: null,
            cbdMg: null,
            cbdMin: null,
            cbdMax: null,
            compareAtPrice: null,
            unitCost: null,
            classificationName: null,
          };
        }
      }

      setCartItemDetails(details);
      setLoadingProductDetails(false);
    };

    fetchProductDetails();
  }, [state.items, state.storeCovaLocationId, API_BASE]);

  // Calculate item count for badge
  const itemCount = state.items.reduce((sum, item) => sum + item.quantity, 0);

  // Tooltip state for dried cannabis equiv.
  const [showTooltip, setShowTooltip] = useState(false);

  /* Order-summary collapse state. The footer used to render every breakdown
     row (Subtotal → Taxes) at all times, which on tall carts pushed the
     products list off the visible area entirely. Default to collapsed so
     the products list always has room; user can expand for the full
     breakdown. */
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // Ref for scrollable container (used by ScrollFade)
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // "Before you go" merchant cards
  const [merchProducts, setMerchProducts] = useState<any[]>([]);
  const [loadingMerch, setLoadingMerch] = useState(false);
  const [selectedMerchColor, setSelectedMerchColor] = useState<Record<string, string | null>>({});
  const [selectedMerchSize, setSelectedMerchSize] = useState<Record<string, string | null>>({});
  const [hoveredMerch, setHoveredMerch] = useState<string | null>(null);
  const recommendedScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRecommendedLeft, setCanScrollRecommendedLeft] = useState(false);
  const [canScrollRecommendedRight, setCanScrollRecommendedRight] = useState(true);
  const prevCartOpenRef = useRef(false);

  const CART_REC_DISMISS_SS = "dank:cart-recommendations:dismiss-session";
  const [cartRecDismissed, setCartRecDismissed] = useState(false);
  const [cartRecommendationItems, setCartRecommendationItems] = useState<any[]>(
    []
  );
  const [cartRecommendationPriceMap, setCartRecommendationPriceMap] = useState<
    Record<string, number>
  >({});
  const [loadingCartRec, setLoadingCartRec] = useState(false);

  useEffect(() => {
    try {
      if (
        typeof window !== "undefined" &&
        sessionStorage.getItem(CART_REC_DISMISS_SS) === "1"
      ) {
        setCartRecDismissed(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Stable key derived from which products are in the cart (ignores quantity changes).
  // Changing quantities produces a new state.items reference but the same sorted ID string,
  // so the recommendations effect below won't re-fire on every +/- tap.
  const cartProductIdKey = useMemo(
    () => state.items.map((i) => String(i.productId)).sort().join(","),
    [state.items]
  );

  // Stable string of boosted categories, also derived from product identity, not quantity.
  const cartPrimaryCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const d of Object.values(cartItemDetails)) {
      const c = d.classificationName?.trim();
      if (c) cats.add(c);
    }
    return Array.from(cats).sort().join(",");
  }, [cartItemDetails]);

  // Contextual recommendations (#43): best sellers at store, excluding cart lines; boost complementary categories.
  useEffect(() => {
    if (
      !isCartOpen ||
      cartRecDismissed ||
      !state.storeCovaLocationId ||
      cartProductIdKey === ""
    ) {
      return;
    }

    const storeId = state.storeCovaLocationId;
    const exclude = new Set(cartProductIdKey.split(","));
    const primary = new Set(cartPrimaryCategories.split(",").filter(Boolean));

    let cancelled = false;
    setLoadingCartRec(true);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/store/${storeId}/products?limit=80&page=1&sortBy=best-selling`,
          { cache: "no-store" }
        );
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!json?.success || cancelled) return;
        const ranked = rankStoreRecommendations(json.items || [], {
          excludeProductIds: exclude,
          primaryClassifications: primary,
          limit: 12,
        });
        if (cancelled) return;
        setCartRecommendationItems(ranked);
        setCartRecommendationPriceMap(
          (json.priceMap as Record<string, number>) || {}
        );
      } catch {
        if (!cancelled) setCartRecommendationItems([]);
      } finally {
        if (!cancelled) setLoadingCartRec(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isCartOpen,
    cartRecDismissed,
    state.storeCovaLocationId,
    cartProductIdKey,
    cartPrimaryCategories,
    API_BASE,
  ]);

  useEffect(() => {
    if (
      !isCartOpen ||
      cartRecDismissed ||
      cartRecommendationItems.length === 0
    ) {
      return;
    }
    const fp = state.items
      .map((i) => i.productId)
      .sort()
      .join(",");
    trackFunnelEventOnce(
      `rec-module-view:cart:${state.storeCovaLocationId}:${fp}`,
      {
        event: "funnel_recommendation_module_view",
        placement: "cart",
        product_count: cartRecommendationItems.length,
      },
      8000
    );
  }, [
    isCartOpen,
    cartRecDismissed,
    cartRecommendationItems.length,
    state.storeCovaLocationId,
    state.items,
  ]);

  // Fetch merchant products when cart opens
  useEffect(() => {
    const fetchMerchProducts = async () => {
      const storeId = state.storeCovaLocationId;
      if (!isCartOpen || !storeId || loadingMerch || merchProducts.length > 0) {
        return;
      }

      setLoadingMerch(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/homepage/merch?covaLocationId=${storeId}`,
          { cache: "no-store" }
        );
        
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.products)) {
            setMerchProducts(data.products || []);
            
            // Initialize first color and size for all products
            const initialColor: Record<string, string | null> = {};
            const initialSize: Record<string, string | null> = {};
            
            data.products.forEach((product: any) => {
              const colors = extractColorsFromProduct(product);
              const sizes = extractSizesFromProduct(product);
              
              if (colors.length > 0) {
                initialColor[product.id] = colors[0];
              }
              if (sizes.length > 0) {
                initialSize[product.id] = sizes[0];
              }
            });
            
            setSelectedMerchColor(initialColor);
            setSelectedMerchSize(initialSize);
          }
        }
      } catch (error) {
        console.error("Failed to fetch merchant products:", error);
      } finally {
        setLoadingMerch(false);
      }
    };

    fetchMerchProducts();
  }, [isCartOpen, state.storeCovaLocationId, API_BASE, loadingMerch, merchProducts.length]);

  useEffect(() => {
    const openedNow = isCartOpen && !prevCartOpenRef.current;
    if (openedNow) {
      trackFunnelEventOnce(
        `cart-open:${state.items.length}:${total.toFixed(2)}`,
        {
          event: "funnel_cart_open",
          cart_value_bucket: getCartValueBucket(total),
          source: "cart_drawer",
        },
        1500
      );
    }
    prevCartOpenRef.current = isCartOpen;
  }, [isCartOpen, state.items.length, total]);

  // Check scroll position for recommended products (used for left/right arrow visibility)
  const checkRecommendedScrollability = useCallback(() => {
    if (recommendedScrollRef.current) {
      const el = recommendedScrollRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const hasLeft = scrollLeft > 2;
      const hasRight = scrollLeft < scrollWidth - clientWidth - 2;
      setCanScrollRecommendedLeft(hasLeft);
      setCanScrollRecommendedRight(hasRight);
    }
  }, []);

  useEffect(() => {
    if (merchProducts.length === 0 && cartRecommendationItems.length === 0) return;
    let rafId: number;
    let cleanup: (() => void) | undefined;
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = recommendedScrollRef.current;
        if (container) {
          checkRecommendedScrollability();
          container.addEventListener("scroll", checkRecommendedScrollability, { passive: true });
          container.addEventListener("scrollend", checkRecommendedScrollability);
          window.addEventListener("resize", checkRecommendedScrollability);
          cleanup = () => {
            container.removeEventListener("scroll", checkRecommendedScrollability);
            container.removeEventListener("scrollend", checkRecommendedScrollability);
            window.removeEventListener("resize", checkRecommendedScrollability);
          };
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafId);
      cleanup?.();
    };
  }, [merchProducts.length, cartRecommendationItems.length, checkRecommendedScrollability]);

  const scrollRecommended = (direction: "left" | "right") => {
    const container = recommendedScrollRef.current;
    if (!container) return;
    const scrollAmount = 280;
    const currentScroll = container.scrollLeft;
    const newScroll =
      direction === "left"
        ? Math.max(0, currentScroll - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, currentScroll + scrollAmount);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
    // Update arrow state after smooth scroll (and on mobile when scroll events may be sparse)
    setTimeout(checkRecommendedScrollability, 400);
  };

  const showCartContextualRec =
    state.items.length > 0 &&
    !cartRecDismissed &&
    (loadingCartRec || cartRecommendationItems.length > 0);
  const showMerchFallback =
    state.items.length > 0 &&
    !cartRecDismissed &&
    merchProducts.length > 0 &&
    !loadingCartRec &&
    cartRecommendationItems.length === 0;

  return (
    <div>
      {isCartOpen && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[1000] flex justify-end bg-[#07063CBF] overflow-hidden touch-none"
          style={{ overscrollBehavior: "none" }}
          onClick={closeCart}
        >
          {/* Drawer panel, must size itself against the viewport. Was using
              h-[calc(100% - 8px)] which Tailwind silently dropped (spaces not
              allowed in arbitrary values), leaving the panel with no explicit
              height. Use viewport-dynamic height (svh) so iOS Safari's URL bar
              doesn't steal pixels, fall back to 100vh otherwise, and tighten
              margins on phones so the footer stops eating the products list. */}
          <div
            className="flex h-[calc(100svh-1rem)] max-h-[calc(100vh-1rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-constant-white px-4 shadow-2xl m-2 sm:m-2"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header, flex-shrink-0 so it never compresses, but padding scales
                down on short / small screens so the products list has room. */}
            <div className="flex-shrink-0 border-b border-neutral-10 pt-4 pb-3 sm:pt-6 sm:pb-4 bg-constant-white rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-h4-mobile font-medium text-text-primary">
                    Your cart
                  </h3>
                  {itemCount > 0 && (
                    <div className="w-6 h-6 rounded-[11px] bg-blue-80 flex items-center justify-center">
                      <span className="text-body-12-desktop font-bold text-constant-white">
                        {itemCount}
                      </span>
                    </div>
                  )}
                </div>
                <ModalCloseButton
                  onClick={closeCart}
                  className="flex-shrink-0"
                  aria-label="Close cart"
                />
              </div>

              {storeDetails && (
                <div className="flex items-center gap-2 text-body-14-desktop sm:font-medium font-normal text-blue-60">
                  <span>
                    {storeDetails.name}
                    {storeDetails.city ? ` - ${storeDetails.city}` : ""}
                  </span>
                  <div className="w-1 h-1 bg-neutral-15" />
                  <Button raw
                    type="button"
                    onClick={() => {
                      // Close the cart, then open the location modal in the user's
                      // current fulfillment mode (delivery → postal modal, pickup → store modal).
                      closeCart();
                      const eventName =
                        mode === "delivery"
                          ? DANK_OPEN_DELIVERY_POSTAL_MODAL
                          : DANK_OPEN_PICKUP_STORE_MODAL;
                      window.dispatchEvent(new CustomEvent(eventName));
                    }}
                    className="text-body-14-desktop sm:font-medium font-normal text-blue-60 transition-colors focus:outline-none"
                  >
                    Change store
                  </Button>
                </div>
              )}
            </div>

            {/* Scrollable products area. `min-h-0` is required on a flex child
                with `overflow-y-auto`, otherwise the flex item's default
                min-height: auto refuses to shrink and the footer gets pushed
                off-screen on short viewports. */}
            <div className="relative flex-1 min-h-0 overflow-x-hidden -mr-2">
              {/* Scrollable Product List Area */}
              <div
                ref={scrollContainerRef}
                className="overflow-y-auto scrollbar-hide h-full"
              >
                <div className="pt-4">
                  {banner &&
                    (banner.type === "success" ? (
                      <div className="mb-4 rounded-lg border border-green-50 bg-green-5 px-3 py-2 text-body-14-desktop font-medium text-green-60">
                        {banner.text}
                      </div>
                    ) : (
                      <CheckoutRecoveryError
                        className="mb-4"
                        title={ERROR_TITLE_CART_CHECKOUT_FAILED}
                        message={banner.text}
                        referenceId={banner.referenceId}
                        supportContext="cart"
                        onDismiss={() => setBanner(null)}
                        onRetry={confirmCheckout}
                        onEditDetails={() => {
                          closeCart();
                          router.push(hasCheckoutRecovery ? "/checkout?resume=1" : "/checkout");
                        }}
                        editDetailsLabel={CTA_OPEN_FULL_CHECKOUT}
                        additionalActions={
                          <Link
                            href="/faq"
                            className="text-[11px] font-semibold text-blue-100 underline-offset-2 hover:underline"
                          >
                            Checkout &amp; delivery FAQs
                          </Link>
                        }
                      />
                    ))}

                  {state.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center sm:pt-24 pt-12 px-4 m-auto h-full">
                      {/* Light purple-blue square with magnifying glass icon */}
                      <div className="w-16 h-16 mb-6 flex items-center justify-center rounded-xl bg-blue-10">
                        <SearchIcon size={40} color="#FFFFFF" />
                      </div>
                      <h3 className="text-h4-mobile font-bold text-text-primary mb-2">
                        Your cart is empty
                      </h3>
                      <p className="text-body-14-desktop font-medium text-text-secondary text-center max-w-xs mb-6">
                        Looks like you haven't added any dank goodies yet. Check
                        out our menu to find your favorites!
                      </p>
                      <Button
                        type="button"
                        variant="brand"
                        size="lg"
                        onClick={() => { closeCart(); router.push("/search"); }}
                        className="mt-2 rounded-xl"
                        startIcon={
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        }
                      >
                        Start Shopping
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Cart Items - Matching image exactly */}
                      {state.items.map((it) => {
                        const key = `${it.productId}:${it.packageId ?? ""}`;
                        const details = cartItemDetails[key];
                        const itemTotal = it.price * it.quantity;
                        const hasDiscount =
                          details?.compareAtPrice &&
                          details.compareAtPrice > it.price;
                        const originalTotal = details?.compareAtPrice
                          ? details.compareAtPrice * it.quantity
                          : null;

                        // Format THC/CBD range
                        const formatTHCRange = () => {
                          if (
                            details?.thcPercent !== null &&
                            details?.thcPercent !== undefined &&
                            Number(details.thcPercent) > 0
                          ) {
                            return `${Number(details.thcPercent).toFixed(1)}%`;
                          }
                          if (
                            details?.thcMg !== null &&
                            details?.thcMg !== undefined &&
                            Number(details.thcMg) > 0
                          ) {
                            return `${(Number(details.thcMg) / 10).toFixed(1)}%`;
                          }
                          if (
                            details?.thcMin !== null &&
                            details?.thcMin !== undefined &&
                            details?.thcMax !== null &&
                            details?.thcMax !== undefined
                          ) {
                            return `${details.thcMin}-${details.thcMax}%`;
                          } else if (
                            details?.thcMin !== null &&
                            details?.thcMin !== undefined
                          ) {
                            return `${details.thcMin}%`;
                          }
                          return null;
                        };

                        const formatCBDRange = () => {
                          if (
                            details?.cbdPercent !== null &&
                            details?.cbdPercent !== undefined &&
                            Number(details.cbdPercent) > 0
                          ) {
                            return `${Number(details.cbdPercent).toFixed(1)}%`;
                          }
                          if (
                            details?.cbdMg !== null &&
                            details?.cbdMg !== undefined &&
                            Number(details.cbdMg) > 0
                          ) {
                            return `${(Number(details.cbdMg) / 10).toFixed(1)}%`;
                          }
                          if (
                            details?.cbdMin !== null &&
                            details?.cbdMin !== undefined &&
                            details?.cbdMax !== null &&
                            details?.cbdMax !== undefined
                          ) {
                            return `${details.cbdMin}-${details.cbdMax}%`;
                          } else if (
                            details?.cbdMin !== null &&
                            details?.cbdMin !== undefined
                          ) {
                            return `${details.cbdMin}%`;
                          }
                          return null;
                        };

                        const thcRange = formatTHCRange();
                        const cbdRange = formatCBDRange();

                        return (
                          <div
                            key={key}
                            className="flex gap-4 pb-4 border-b border-neutral-10 relative items-center"
                          >
                            {/* Product Image */}
                            <div className="w-[98px] h-[98px] rounded-lg overflow-hidden bg-bg-2 flex-shrink-0 border border-neutral-10">
                              {details?.imageUrl ? (
                                <img
                                  src={details.imageUrl}
                                  alt={it.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <CartIcon
                                    size={24}
                                    className="text-text-disabled"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col justify-between flex-1 min-w-0">
                              {/* Product Details */}
                              <div className="flex-1 min-w-0">
                                {/* Brand Name */}
                                {details?.brandName && (
                                  <div className="text-body-14-desktop font-medium text-text-secondary">
                                    {details.brandName}
                                  </div>
                                )}

                                {/* Product Name */}
                                <div className="text-body-16-desktop font-bold text-text-primary">
                                  {it.name}
                                </div>

                                {/* THC/CBD Details */}
                                {(thcRange || cbdRange) && (
                                  <div className="text-body-12-desktop font-medium text-text-secondary">
                                    {thcRange && <span>THC: {thcRange}</span>}
                                    {thcRange && cbdRange && <span> </span>}
                                    {cbdRange && <span>CBD: {cbdRange}</span>}
                                  </div>
                                )}
                              </div>

                              {/* Quantity Controls and Price*/}
                              <div className="flex justify-between w-full items-center gap-1 mt-1 pr-2">
                                {/* Quantity Selector */}
                                <div className="flex items-center gap-1.5">
                                  <Button raw
                                    type="button"
                                    disabled={submitting || it.quantity <= 1}
                                    onClick={() => updateQty(it.productId, it.packageId ?? null, it.quantity - 1)}
                                    className="flex h-6 w-6 items-center justify-center rounded-lg border border-neutral-10 bg-constant-white text-sm font-semibold select-none text-text-primary transition-all disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    −
                                  </Button>
                                  <span className="w-6 text-center text-sm font-semibold text-text-primary">
                                    {it.quantity}
                                  </span>
                                  <Button raw
                                    type="button"
                                    disabled={submitting || (it.inStock != null && it.quantity >= it.inStock)}
                                    onClick={() => updateQty(it.productId, it.packageId ?? null, it.quantity + 1)}
                                    className="flex h-6 w-6 items-center justify-center rounded-lg border border-blue-100 bg-blue-100 text-sm font-semibold select-none text-constant-white transition-all hover:border-neutral-70 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    +
                                  </Button>
                                </div>

                                {/* Price */}
                                <div className="text-right">
                                  {hasDiscount && originalTotal ? (
                                    <div className="flex flex-col items-end gap-1">
                                      <span className="text-body-18 font-bold text-text-primary">
                                        ${itemTotal.toFixed(2)}
                                      </span>
                                      <span className="text-body-14-desktop font-medium text-text-tertiary line-through">
                                        ${originalTotal.toFixed(2)}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="text-body-18 font-bold text-text-primary">
                                      ${itemTotal.toFixed(2)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Trash Icon - Right Section */}
                            <div className="absolute right-0 top-0 flex-shrink-0">
                              <Button raw
                                type="button"
                                disabled={submitting}
                                onClick={() => removeItem(it.productId, it.packageId)}
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-neutral-40 select-none transition-all disabled:opacity-50"
                                aria-label="Remove item"
                              >
                                <TrashIcon size={15} color="currentColor" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Contextual cart recommendations + merch fallback (#43) */}
                  {showCartContextualRec && (
                    <div className="my-6">
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-2 px-2">
                        <div>
                          <h3 className="text-body-18 font-bold text-text-primary">
                            You might also like
                          </h3>
                          <p className="mt-0.5 text-body-12-desktop text-text-secondary">
                            Picks for your cart, in stock at this store.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-body-12-desktop text-text-secondary">
                            Helpful?
                          </span>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop font-semibold text-text-primary hover:border-green-30 transition-colors"
                            onClick={() => {
                              trackFunnelEvent({ event: "funnel_recommendation_feedback", placement: "cart", helpful: true });
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            Yes
                          </Button>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop font-semibold text-text-primary hover:border-red-20 transition-colors"
                            onClick={() => {
                              trackFunnelEvent({ event: "funnel_recommendation_feedback", placement: "cart", helpful: false });
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            No
                          </Button>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop text-text-secondary transition-colors"
                            aria-label="Hide suggestions"
                            onClick={() => {
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            Hide
                          </Button>
                        </div>
                      </div>

                      {loadingCartRec && cartRecommendationItems.length === 0 ? (
                        <div className="mx-2 h-40 animate-pulse rounded-xl bg-neutral-10" />
                      ) : (
                        <div className="relative">
                          {canScrollRecommendedLeft && (
                            <Button raw
                              type="button"
                              onClick={() => scrollRecommended("left")}
                              className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-md transition-all hover:border-blue-20 focus:outline-none"
                              aria-label="Scroll suggestions left"
                            >
                              <ChevronLeftIcon size={18} color="#343B91" />
                            </Button>
                          )}
                          <div
                            ref={recommendedScrollRef}
                            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-2 pb-1"
                            style={{
                              scrollbarWidth: "none",
                              msOverflowStyle: "none",
                            }}
                          >
                            {cartRecommendationItems.map((item: any) => {
                              const filteredItem = {
                                ...item,
                                sizeOptions: item.sizeOptions
                                  ? item.sizeOptions.filter(
                                      (opt: any) =>
                                        Number(opt?.inStock ?? 0) >= 1
                                    )
                                  : undefined,
                              };
                              const price =
                                cartRecommendationPriceMap[item.productId] ??
                                (filteredItem.sizeOptions &&
                                filteredItem.sizeOptions.length > 0
                                  ? filteredItem.sizeOptions[0].price
                                  : item.unitCost ?? 0);
                              return (
                                <div
                                  key={item.id}
                                  className="min-w-[264px] max-w-[282px] flex-shrink-0"
                                >
                                  <ProductCard
                                    item={filteredItem}
                                    price={
                                      typeof price === "number" ? price : 0
                                    }
                                    relatedProducts
                                    analyticsSource="cart_recommendations"
                                  />
                                </div>
                              );
                            })}
                          </div>
                          {canScrollRecommendedRight && (
                            <Button raw
                              type="button"
                              onClick={() => scrollRecommended("right")}
                              className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-md transition-all hover:border-blue-20 focus:outline-none"
                              aria-label="Scroll suggestions right"
                            >
                              <ChevronRightIcon size={18} color="#343B91" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {showMerchFallback && (
                    <div className="my-6">
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-2 px-2">
                        <div>
                          <h3 className="text-body-18 font-bold text-text-primary">
                            You might also like
                          </h3>
                          <p className="mt-0.5 text-body-12-desktop text-text-secondary">
                            Picks for your cart, in stock at this store.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-body-12-desktop text-text-secondary">Helpful?</span>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop font-semibold text-text-primary hover:border-green-30 transition-colors"
                            onClick={() => {
                              trackFunnelEvent({ event: "funnel_recommendation_feedback", placement: "cart_merch", helpful: true });
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            Yes
                          </Button>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop font-semibold text-text-primary hover:border-red-20 transition-colors"
                            onClick={() => {
                              trackFunnelEvent({ event: "funnel_recommendation_feedback", placement: "cart_merch", helpful: false });
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            No
                          </Button>
                          <Button raw
                            type="button"
                            className="rounded-lg border border-neutral-10 px-2 py-1 text-body-12-desktop text-text-secondary transition-colors"
                            aria-label="Hide suggestions"
                            onClick={() => {
                              try { sessionStorage.setItem(CART_REC_DISMISS_SS, "1"); } catch { /* ignore */ }
                              setCartRecDismissed(true);
                            }}
                          >
                            Hide
                          </Button>
                        </div>
                      </div>

                      <div className="relative">
                        {canScrollRecommendedLeft && (
                          <Button raw
                            type="button"
                            onClick={() => scrollRecommended("left")}
                            className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-md transition-all hover:border-blue-20 focus:outline-none"
                            aria-label="Scroll recommended products left"
                          >
                            <ChevronLeftIcon size={18} color="#343B91" />
                          </Button>
                        )}

                        <div
                          ref={recommendedScrollRef}
                          className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-2"
                          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                          {merchProducts.map((product: any) => {
                            const productTitle = product?.name || product?.masterProductName || product?.classificationName || "Product";
                            const slug = product?.slug || (Array.isArray(product?.groupProducts) && product.groupProducts[0]?.slug) || "";
                            const productHref = slug ? `/product/${slug}` : null;
                            
                            const colors = extractColorsFromProduct(product);
                            const sizes = extractSizesFromProduct(product);
                            const selectedColor = selectedMerchColor[product.id] || (colors.length > 0 ? colors[0] : null);
                            const selectedSize = selectedMerchSize[product.id] || (sizes.length > 0 ? sizes[0] : null);
                            
                            const selectedOption = getSelectedVariantOption(product, selectedColor, selectedSize);
                            const displayPrice = selectedOption?.price || 0;
                            const displayImage = selectedOption?.heroShotUri 
                              ? ensureAbsoluteImageUrl(selectedOption.heroShotUri) || "/media/brand/logo.webp"
                              : ensureAbsoluteImageUrl(product?.heroShotUri) || "/media/brand/logo.webp";
                            
                            return (
                              <div
                                key={product.id}
                                className="flex-shrink-0 w-[240px] overflow-hidden bg-constant-white"
                                onMouseEnter={() => setHoveredMerch(product.id)}
                                onMouseLeave={() => setHoveredMerch(null)}
                              >
                                <div className="flex flex-col">
                                  {/* Product Image */}
                                  <div className="relative w-full aspect-square bg-neutral-5 overflow-hidden rounded-2xl">
                                    <img
                                      src={displayImage}
                                      alt={productTitle}
                                      className="absolute inset-0 h-full w-full object-cover object-top"
                                      loading="lazy"
                                      decoding="async"
                                      fetchPriority="low"
                                      onError={(e) => {
                                        const el = e.currentTarget as HTMLImageElement;
                                        if (!el.src.includes("/media/brand/logo.webp")) {
                                          el.src = "/media/brand/logo.webp";
                                          el.className = "absolute inset-0 h-full w-full object-contain opacity-30";
                                        }
                                      }}
                                    />

                                    {/* Hover Overlay with Color & Size Selection */}
                                    {hoveredMerch === product.id && (colors.length > 1 || sizes.length > 1) && (
                                      <>
                                        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(23,22,90,0.5)] to-[rgba(52,59,145,0.2)] opacity-70 transition-opacity" />
                                        <div className="absolute left-2 bottom-2 right-2 flex flex-col gap-1.5">
                                          {/* Color Chips */}
                                          {colors.length > 1 && (
                                            <div className="flex flex-wrap gap-1">
                                              {colors.map((color) => {
                                                const isSelected = selectedColor === color;
                                                return (
                                                  <Button raw
                                                    type="button"
                                                    key={color}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      setSelectedMerchColor((prev) => ({ ...prev, [product.id]: color }));
                                                    }}
                                                    className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-semibold select-none transition-all duration-200 active:scale-[0.99] ${
                                                      isSelected
                                                        ? "border-blue-80 bg-blue-80 text-constant-white hover:border-blue-80"
                                                        : "border-blue-10 bg-constant-white text-blue-100 hover:border-neutral-70"
                                                    }`}
                                                    title={color}
                                                  >
                                                    {color}
                                                  </Button>
                                                );
                                              })}
                                            </div>
                                          )}
                                          
                                          {/* Size Options */}
                                          {sizes.length > 1 && (
                                            <div className="w-full overflow-hidden rounded-lg border border-neutral-10 bg-constant-white shadow-lg">
                                              <div className="flex w-max gap-1.5 px-1.5 py-1 overflow-x-auto scrollbar-hide">
                                              {sizes.map((size, index) => {
                                                const isSelected = selectedSize === size;
                                                return (
                                                  <Button raw
                                                    type="button"
                                                    key={size}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      setSelectedMerchSize((prev) => ({ ...prev, [product.id]: size }));
                                                    }}
                                                    className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-semibold select-none transition-all duration-200 active:scale-[0.99] ${
                                                      isSelected
                                                        ? "border-blue-80 bg-blue-80 text-constant-white hover:border-blue-80"
                                                        : "border-blue-10 bg-constant-white text-blue-100 hover:border-neutral-70"
                                                    }`}
                                                    title={size}
                                                  >
                                                    {size}
                                                  </Button>
                                                );
                                              })}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>

                                  {/* Product Info */}
                                  <div className="flex items-center justify-between py-3">
                                    <div className="flex flex-col gap-0.5 flex-1 max-w-[160px]">
                                      {productHref ? (
                                        <Link
                                          href={productHref}
                                          onClick={() =>
                                            trackFunnelEventOnce(
                                              `merch-click:${product.id}`,
                                              {
                                                event: "funnel_product_click",
                                                product_id: String(
                                                  selectedOption?.productId ??
                                                    product.id
                                                ),
                                                source: "cart_merch_fallback",
                                              },
                                              1200
                                            )
                                          }
                                        >
                                          <h4 className="text-sm font-medium transition-colors overflow-hidden text-ellipsis whitespace-nowrap text-text-primary">
                                            {productTitle}
                                          </h4>
                                        </Link>
                                      ) : (
                                        <h4 className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap text-text-primary">
                                          {productTitle}
                                        </h4>
                                      )}
                                      <p className="text-base font-bold text-text-primary">
                                        ${Number(displayPrice || 0).toFixed(2)}
                                      </p>
                                    </div>

                                    {/* Add to Cart Button */}
                                    <Button raw
                                      type="button"
                                      disabled={!selectedOption?.actualPackageId}
                                      title={selectedOption?.actualPackageId ? "Add to cart" : "Unavailable"}
                                      aria-label={selectedOption?.actualPackageId ? "Add to cart" : "Unavailable"}
                                      onClick={() => {
                                        if (!selectedOption?.actualPackageId) return;
                                        addItem({
                                          productId: selectedOption.productId,
                                          name: productTitle,
                                          catalogItemId: selectedOption.productId,
                                          packageId: selectedOption.packageId || selectedOption.actualPackageId,
                                          price: selectedOption.price,
                                          actualPackageId: selectedOption.actualPackageId,
                                          inStock: selectedOption.inStock,
                                        } as any);
                                        trackFunnelEvent({
                                          event: "funnel_add_to_cart",
                                          product_id: String(
                                            selectedOption.productId
                                          ),
                                          cart_value_bucket: getCartValueBucket(
                                            selectedOption.price
                                          ),
                                          source: "cart_merch_fallback",
                                        });
                                      }}
                                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition-all ${
                                        selectedOption?.actualPackageId
                                          ? "border border-blue-100 bg-blue-100 text-constant-white hover:border-neutral-70"
                                          : "cursor-not-allowed bg-neutral-10 text-neutral-40"
                                      }`}
                                    >
                                      <CartIcon size={17} color="currentColor" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Right Arrow */}
                        {canScrollRecommendedRight && (
                          <Button raw
                            type="button"
                            onClick={() => scrollRecommended("right")}
                            className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-md transition-all hover:border-blue-20 focus:outline-none"
                            aria-label="Scroll recommended products right"
                          >
                            <ChevronRightIcon size={18} color="#343B91" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Gradient overlay - automatically shows when not scrolled to bottom */}
              <ScrollFade 
                targetRef={scrollContainerRef} 
                height={64}
                backgroundColor="#FFFFFF"
              />
            </div>

            {/* Fixed Footer, flex-shrink-0 keeps it pinned at the bottom.
                Vertical padding scales down on short / small screens so the
                products list above keeps as much room as possible.

                The breakdown (Subtotal → Taxes) is wrapped in a collapsible
                region. Default is collapsed so the products list stays
                visible on short viewports; the user toggles "Show details"
                on the Estimated-total row to expand the full receipt. Critical
                bits (THC pickup-blocked banner, Apt/unit input, Estimated
                total, promo code, checkout button) stay always-visible. */}
            {state.items.length > 0 && (
              <div className="flex-shrink-0 border-t border-neutral-10 bg-constant-white rounded-b-2xl">
                <div className="py-3 sm:py-4 space-y-1.5 sm:space-y-2">
                  {/* Order Summary (collapsible breakdown) */}
                  <div className="space-y-2">
                    {summaryExpanded && (
                      <>
                    <div className="flex justify-between text-body-14-desktop font-medium">
                      <span className="text-text-secondary">Subtotal</span>
                      <span className="text-text-primary">
                        ${total.toFixed(2)}
                      </span>
                    </div>

                    <PromoSavingsBanner promo={state.promoCode} />

                    {/* Dried cannabis equiv. - with red highlighting when over limit */}
                    {thcLimit && (
                      <div
                        className={`flex items-center justify-between text-body-14-desktop font-medium rounded-lg`}
                      >
                        <div className="flex items-center gap-1 relative">
                          <span className={mode === "pickup" && thcLimit.current > thcLimit.max ? "text-red-50" : "text-text-secondary"}>
                            Dried cannabis equiv.
                          </span>
                          <div
                            className="relative inline-flex items-center"
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                          >
                            <InfoIcon
                              size={16}
                              color={mode === "pickup" && thcLimit.current > thcLimit.max ? "#FF4B4B" : "#5A5A89"}
                              className="cursor-help"
                            />
                            {showTooltip && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 p-3 bg-constant-dark text-constant-white text-body-12-desktop font-normal rounded-lg shadow-xl z-[1000] pointer-events-none whitespace-normal">
                                <p>
                                  {mode === "pickup"
                                    ? "Alberta regulation limits pickup orders to 30g of dried cannabis equivalent per transaction"
                                    : "No order limits for delivery! Large orders are automatically split into 30g packs to comply with regulations"}
                                </p>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-constant-dark"></div>
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={mode === "pickup" && thcLimit.current > thcLimit.max ? "text-red-50 font-bold" : "text-text-primary"}>
                          {Math.round(thcLimit.current)} / {thcLimit.max}g limit
                        </span>
                      </div>
                    )}
                      </>
                    )}
                    {/* THC pickup-blocked banner, always visible regardless of
                        collapse state; it's an action-required warning. */}
                    {thcPickupBlocked && (
                      <div
                        role="status"
                        className="mt-2 overflow-hidden rounded-xl border border-l-4 border-red-15 border-l-red-50 bg-red-5"
                      >
                        <div className="flex gap-3 px-3.5 py-3">
                          <div
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-constant-white"
                            aria-hidden
                          >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-body-14-desktop font-bold leading-tight text-red-90">
                              Pickup 30g limit reached
                            </p>
                            <p className="mt-0.5 text-xs leading-snug text-red-80">
                              Remove a pack or switch to delivery, large orders split automatically.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Collapsible: Listed savings → Taxes. Apt/unit stays
                        always-visible right below (it's an input the user
                        often needs without expanding the summary). */}
                    {summaryExpanded && productDiscount > 0 && (
                      <div className="space-y-0.5">
                        <div className="flex justify-between text-body-12-desktop font-medium text-text-secondary">
                          <span className="pr-2">Listed savings (reference)</span>
                          <span className="shrink-0 text-green-60">
                            ~${productDiscount.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-[11px] leading-snug text-text-tertiary">
                          Compared to standard cost, your subtotal above is the price you pay.
                        </p>
                      </div>
                    )}

                    {/* Apt/unit field, always visible for delivery mode. */}
                    {mode === "delivery" && (
                      <div className="space-y-1.5 rounded-xl border border-neutral-10 bg-bg-1 px-3 py-2.5">
                        <label
                          htmlFor="cart-drawer-delivery-unit"
                          className="block text-body-12-desktop font-medium text-text-secondary"
                        >
                          Apt / unit{" "}
                          <span className="font-normal text-text-tertiary">(optional)</span>
                        </label>
                        <input
                          id="cart-drawer-delivery-unit"
                          type="text"
                          value={deliveryUnit}
                          onChange={(e) => setDeliveryUnit(e.target.value)}
                          placeholder="Suite, apartment, floor"
                          disabled={submitting}
                          autoComplete="address-line2"
                          className="w-full rounded-lg border border-neutral-10 bg-constant-white px-3 py-2.5 text-sm font-medium text-text-primary placeholder:text-text-tertiary focus:border-blue-80 focus:outline-none focus:ring-2 focus:ring-blue-80/20 disabled:cursor-not-allowed disabled:bg-bg-1 disabled:text-text-tertiary"
                        />
                        <p className="text-[11px] leading-snug text-text-tertiary">
                          Used for same-day routing so drivers can allow extra time for elevators and
                          building access.
                        </p>
                      </div>
                    )}

                    {summaryExpanded && (
                      <>
                    {mode === "delivery" && (
                      <div className="space-y-0.5">
                        <div className="flex justify-between text-body-14-desktop font-medium">
                          <span className="text-text-secondary">
                            {deliveryType === "same-day"
                              ? "Delivery fee (same-day)"
                              : "Delivery fee (mail order)"}
                          </span>
                          <span className="text-text-primary font-medium" aria-live="polite">
                            {deliveryFeeLoading ? (
                              <span className="animate-pulse text-text-tertiary">…</span>
                            ) : !postalForEstimate ? (
                              <span className="text-sm font-normal text-text-tertiary">
                                Add postal code
                              </span>
                            ) : (
                              `$${deliveryFee.toFixed(2)}`
                            )}
                          </span>
                        </div>
                        <p className="text-[11px] leading-snug text-text-tertiary">
                          {PRE_CHECKOUT_DELIVERY_VARIABLE_NOTE}
                        </p>
                      </div>
                    )}

                    {promoDiscount > 0 && (
                      <div className="flex justify-between text-body-14-desktop font-medium">
                        <span className="text-text-secondary">Promo discount</span>
                        <span className="font-semibold text-green-50">
                          −${promoDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {mode === "delivery" && (
                      <div className="flex justify-between text-body-14-desktop font-medium">
                        <span className="text-text-secondary">Driver tip (optional)</span>
                        <span className="text-text-tertiary">$0.00 · at checkout</span>
                      </div>
                    )}

                    <div className="flex justify-between text-body-14-desktop font-medium">
                      <span className="text-text-secondary">{ORDER_TAX_LABEL}</span>
                      <span className="text-text-primary">${tax.toFixed(2)}</span>
                    </div>
                      </>
                    )}

                    {/* Estimated total, always visible. Acts as the
                        collapse/expand toggle on its left side so the user
                        can pull up the full receipt with one tap. */}
                    <div className="flex items-center justify-between border-t border-neutral-10 pt-2 text-body-16 font-bold">
                      <Button raw
                        type="button"
                        onClick={() => setSummaryExpanded((v) => !v)}
                        aria-expanded={summaryExpanded}
                        aria-controls="cart-summary-breakdown"
                        className="inline-flex items-center gap-1 rounded-md text-body-14-desktop font-semibold text-blue-100 transition-colors hover:text-blue-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-80/30"
                      >
                        {summaryExpanded ? (
                          <ChevronUpIcon size={14} color="currentColor" />
                        ) : (
                          <ChevronDownIcon size={14} color="currentColor" />
                        )}
                        <span>{summaryExpanded ? "Hide details" : "Show details"}</span>
                      </Button>
                      <div className="flex items-baseline gap-2">
                        <span className="text-text-primary">Estimated total</span>
                        <span className="text-text-primary">${finalTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    {/* <p className="text-[11px] leading-relaxed text-text-tertiary">
                      {PRE_CHECKOUT_ESTIMATE_FOOTNOTE}
                    </p> */}
                  </div>

                  {/* Promocode, focus ring matches mega header search */}
                  <div>
                    <div className="flex min-h-[48px] items-stretch overflow-hidden rounded-xl border border-neutral-10 bg-constant-white transition-[border-color,box-shadow] focus-within:border-blue-80 focus-within:shadow-[inset_0_0_0_2px_rgba(52,59,145,0.2)]">
                      <input
                        type="text"
                        placeholder="Enter your promocode"
                        value={promoCodeInput}
                        onChange={(e) => {
                          setPromoCodeInput(e.target.value);
                          if (promoCodeError) setPromoCodeError(null);
                        }}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-label="Promo code"
                        aria-invalid={!!promoCodeError}
                        aria-describedby={promoCodeError ? "cart-promo-error" : undefined}
                        className={`min-w-0 flex-[2] border-0 bg-transparent px-3 py-3 text-sm font-medium placeholder:text-text-tertiary focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-bg-1 disabled:text-text-tertiary ${
                          promoCodeError ? "text-red-50" : "text-text-primary"
                        }`}
                        disabled={submitting || !!state.promoCode}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !state.promoCode &&
                            promoCodeInput.trim()
                          ) {
                            e.preventDefault();
                            handleApplyPromoCode();
                          }
                        }}
                      />
                      {!state.promoCode && (
                        <Button raw
                          type="button"
                          onClick={handleApplyPromoCode}
                          disabled={submitting || !promoCodeInput.trim()}
                          className="flex-shrink-0 border-l border-neutral-10 bg-blue-100 px-5 py-3 text-sm font-semibold text-constant-white transition-colors select-none hover:border-l-neutral-70 disabled:cursor-not-allowed disabled:bg-neutral-10 disabled:text-neutral-40 disabled:pointer-events-none"
                        >
                          Apply
                        </Button>
                      )}
                      {state.promoCode && (
                        <Button raw
                          type="button"
                          onClick={handleRemovePromoCode}
                          disabled={submitting}
                          className="flex-shrink-0 border-l border-neutral-10 bg-red-5 px-5 py-3 text-sm font-semibold text-red-60 transition-colors select-none disabled:opacity-50"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    {promoCodeError && (
                      <p id="cart-promo-error" className="mt-1.5 px-1 text-sm font-medium italic text-red-50">
                        {promoCodeError}
                      </p>
                    )}
                  </div>

                  {/* Validation blockers, explicit recovery paths (issue #34) */}
                  {(total < 50 ||
                    (mode === "delivery" &&
                      deliveryType === "same-day" &&
                      !sameDayAvailable)) && (() => {
                    const remaining = Math.max(0, 50 - total);
                    const minBlock = total < 50;
                    const dayBlock =
                      mode === "delivery" &&
                      deliveryType === "same-day" &&
                      !sameDayAvailable;
                    const headline = minBlock
                      ? `Add $${remaining.toFixed(2)} to checkout`
                      : sameDayAvailableReason || "Same-day delivery unavailable";
                    const subtext = minBlock
                      ? `Minimum order is $50.00 · current $${total.toFixed(2)}`
                      : "Try mail-order or pickup instead.";
                    return (
                      <div
                        role="status"
                        className="overflow-hidden rounded-xl border border-l-4 border-yellow-30 border-l-yellow-50 bg-yellow-5"
                      >
                        <div className="flex gap-3 px-3.5 py-3">
                          <div
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-yellow-50 text-constant-white"
                            aria-hidden
                          >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-body-14-desktop font-bold leading-tight text-yellow-90">
                              {headline}
                            </p>
                            <p className="mt-0.5 text-xs leading-snug text-yellow-80">
                              {subtext}
                            </p>
                            {dayBlock && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setDeliveryType("mail-order")}
                                >
                                  {CTA_SWITCH_MAIL_ORDER}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Proceed to Checkout Button */}
                  <Button raw
                    type="button"
                    disabled={
                      submitting ||
                      state.items.length === 0 ||
                      total < 50 ||
                      (mode === "delivery" && deliveryType === "same-day" && !sameDayAvailable) ||
                      (mode === "pickup" && !!thcLimit && thcLimit.current > thcLimit.max)
                    }
                    onClick={() => {
                      trackFunnelEventOnce(
                        `checkout-start:drawer:${state.items.length}:${total.toFixed(2)}`,
                        {
                          event: "funnel_checkout_start",
                          cart_value_bucket: getCartValueBucket(total),
                          source: "cart_drawer_cta",
                          ...tagCartConfidenceUx(),
                        },
                        30000
                      );
                      closeCart();
                      router.push(hasCheckoutRecovery ? "/checkout?resume=1" : "/checkout");
                    }}
                    className="w-full rounded-xl border border-blue-100 bg-blue-100 px-6 py-3.5 text-sm font-semibold select-none text-constant-white transition-all hover:border-neutral-70 disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Processing…
                      </span>
                    ) : CTA_PROCEED_CHECKOUT}
                  </Button>
                  {status && (
                    <div className="mt-2 text-body-14-desktop font-medium text-text-primary text-center">
                      {status}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
